import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Save, Power, AlertTriangle, Check, CalendarClock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const token = localStorage.getItem("grizzly_admin_token");
  const authHeader = { Authorization: `Bearer ${token}` };

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings`, {
        headers: authHeader,
      });
      setSettings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const persist = async (next) => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await axios.put(`${API}/admin/settings`, next, {
        headers: authHeader,
      });
      setSettings(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleMaintenance = () => {
    persist({ maintenance_mode: !settings.maintenance_mode });
  };

  const toggleReservations = () => {
    persist({ reservations_enabled: !settings.reservations_enabled });
  };

  const saveMessages = () => {
    persist({
      maintenance_message_fr: settings.maintenance_message_fr,
      maintenance_message_en: settings.maintenance_message_en,
    });
  };

  if (loading || !settings) {
    return (
      <div className="text-center py-20 text-ivory/50">Chargement...</div>
    );
  }

  const on = settings.maintenance_mode;

  return (
    <div data-testid="admin-settings" className="max-w-3xl space-y-8">
      <div>
        <h2 className="font-serif text-3xl font-light tracking-tight mb-2">
          Paramètres du site
        </h2>
        <p className="text-sm text-ivory/55">
          Gérez la disponibilité publique de votre site.
        </p>
      </div>

      {/* Maintenance toggle card */}
      <div
        className={`border p-6 md:p-8 transition-colors ${
          on ? "border-copper bg-copper/5" : "border-edge bg-surface"
        }`}
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex gap-4">
            <div
              className={`w-12 h-12 flex items-center justify-center border ${
                on ? "border-copper text-copper" : "border-edge text-ivory/50"
              }`}
            >
              {on ? <AlertTriangle size={20} /> : <Power size={20} />}
            </div>
            <div>
              <h3 className="font-serif text-2xl text-ivory">
                Mode maintenance
              </h3>
              <p className="text-sm text-ivory/60 mt-1 max-w-md">
                {on
                  ? "Le site public affiche actuellement la page de maintenance. Les visiteurs ne voient pas le contenu."
                  : "Le site est visible par tous. Activez ce mode pour masquer temporairement le site public."}
              </p>
              <div className="mt-3">
                <span
                  data-testid="maintenance-status"
                  className={`inline-block text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${
                    on
                      ? "text-copper border-copper/40 bg-copper/10"
                      : "text-emerald-400 border-emerald-400/40 bg-emerald-400/5"
                  }`}
                >
                  {on ? "Maintenance activée" : "Site en ligne"}
                </span>
              </div>
            </div>
          </div>

          {/* Switch */}
          <button
            data-testid="maintenance-toggle"
            onClick={toggleMaintenance}
            disabled={saving}
            role="switch"
            aria-checked={on}
            className={`relative w-16 h-8 border transition-colors duration-300 disabled:opacity-60 ${
              on ? "bg-copper border-copper" : "bg-obsidian border-edge"
            }`}
          >
            <span
              className={`absolute top-0.5 w-6 h-6 transition-transform duration-300 ${
                on ? "translate-x-8 bg-obsidian" : "translate-x-0.5 bg-ivory/70"
              }`}
            />
          </button>
        </div>

        {on && (
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="preview-maintenance"
            className="inline-block mt-6 text-xs uppercase tracking-[0.2em] text-copper hover:text-ivory transition-colors"
          >
            Voir la page de maintenance →
          </a>
        )}
      </div>

      {/* Reservations toggle card */}
      {(() => {
        const resaOn = settings.reservations_enabled;
        return (
          <div
            className={`border p-6 md:p-8 transition-colors ${
              resaOn ? "border-edge bg-surface" : "border-copper bg-copper/5"
            }`}
          >
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex gap-4">
                <div
                  className={`w-12 h-12 flex items-center justify-center border ${
                    resaOn
                      ? "border-emerald-400/50 text-emerald-400"
                      : "border-copper text-copper"
                  }`}
                >
                  <CalendarClock size={20} />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-ivory">
                    Réservations en ligne
                  </h3>
                  <p className="text-sm text-ivory/60 mt-1 max-w-md">
                    {resaOn
                      ? "Les clients peuvent réserver une table via le formulaire du site."
                      : "Le formulaire de réservation est masqué. Les clients sont invités à appeler le restaurant."}
                  </p>
                  <div className="mt-3">
                    <span
                      data-testid="reservations-status"
                      className={`inline-block text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${
                        resaOn
                          ? "text-emerald-400 border-emerald-400/40 bg-emerald-400/5"
                          : "text-copper border-copper/40 bg-copper/10"
                      }`}
                    >
                      {resaOn ? "Réservations activées" : "Réservations fermées"}
                    </span>
                  </div>
                </div>
              </div>

              <button
                data-testid="reservations-toggle"
                onClick={toggleReservations}
                disabled={saving}
                role="switch"
                aria-checked={resaOn}
                className={`relative w-16 h-8 border transition-colors duration-300 disabled:opacity-60 ${
                  resaOn ? "bg-copper border-copper" : "bg-obsidian border-edge"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-6 h-6 transition-transform duration-300 ${
                    resaOn
                      ? "translate-x-8 bg-obsidian"
                      : "translate-x-0.5 bg-ivory/70"
                  }`}
                />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Messages editor */}
      <div className="border border-edge bg-surface p-6 md:p-8">
        <h3 className="font-serif text-2xl text-ivory mb-1">
          Message de maintenance
        </h3>
        <p className="text-sm text-ivory/55 mb-6">
          Ce texte s&apos;affiche sur la page de maintenance.
        </p>

        <div className="space-y-6">
          <div>
            <label className="overline block mb-3">Message (Français)</label>
            <textarea
              data-testid="maintenance-message-fr"
              rows={3}
              value={settings.maintenance_message_fr}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  maintenance_message_fr: e.target.value,
                }))
              }
              className="input-elegant resize-none"
            />
          </div>
          <div>
            <label className="overline block mb-3">Message (English)</label>
            <textarea
              data-testid="maintenance-message-en"
              rows={3}
              value={settings.maintenance_message_en}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  maintenance_message_en: e.target.value,
                }))
              }
              className="input-elegant resize-none"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <button
            data-testid="save-messages"
            onClick={saveMessages}
            disabled={saving}
            className="btn-primary disabled:opacity-60"
            style={{ padding: "0.7rem 1.4rem", fontSize: "0.72rem" }}
          >
            <Save size={14} /> {saving ? "Sauvegarde..." : "Enregistrer"}
          </button>
          {saved && (
            <span className="flex items-center gap-2 text-emerald-400 text-sm">
              <Check size={16} /> Enregistré
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
